import _ from 'lodash';
import arrayMove from 'array-move';
import { store } from '../../../../store';
import { ACTION, MODALS, SEARCH_OPTIONS } from '../constants';
import { sortByRateAsc, sortByRateDesc, escapeStringRegexp, replaceHTMLEntities } from '../../../../utils/helper';
import { modifierServiceTypesToObject, modifiersProvidersToObject } from './modifiers';

/**
 *
 * @name getProviderName
 * @description Получаем имя провайдера
 * @param {string} provider_id
 * @param {number} mode
 * @returns {string|Error|*}
 */
export const getProviderName = (provider_id, mode) => {
  const modeToNumber = Number(mode);
  if (_.isNaN(modeToNumber)) {
    return new Error(`Not valid "mode" type, current - ${typeof mode}`);
  }
  if (mode) {
    const {
      services: { providers },
    } = store.getState();
    if (provider_id) {
      const findProvider = providers.find((item) => Number(item.id) === Number(provider_id));
      if (findProvider) {
        return replaceHTMLEntities(findProvider.name);
      }
    }
    return 'error_provider';
  }
  return 'Manual';
};

/**
 *
 * @name getProvider
 * @description Получаем данные по провайдеру
 * @param {number} provider_id
 * @returns {string|Error|*}
 */
export const getProvider = (provider_id) => {
  const providerIdToNumber = Number(provider_id);
  if (_.isNaN(providerIdToNumber)) {
    return new Error(`Not valid "providerIdToNumber" type, current - ${typeof provider_id}`);
  }
  if (provider_id) {
    const providers = modifiersProvidersToObject();
    if (providers[provider_id]) {
      return providers[provider_id];
    }
  }
  return null;
};

/**
 *
 * @name getStatusName
 * @description Получение название статуса
 * @param {number|string} status
 * @returns {string|Error}
 */
export const getStatusName = (status) => {
  if (_.isNumber(status) || _.isNull(status)) {
    switch (status) {
    case 0:
      return 'Disabled';
    case 1:
      return 'Enabled';
    case null:
      return 'Status';
    default:
      return new Error(`Not valid status value, current type - ${typeof status}`);
    }
  }
  return new Error('Not valid status type');
};

/**
 *
 * @name getHeightRow
 * @description Выдаем высоту каждой строке
 * @param {object} row - Категория или Сервис
 * @returns {number}
 */
export const getHeightRow = (row) => {
  if (row.collection === 'category') {
    return 46;
  }
  const rows = Math.ceil(row.name.length / 84);
  const basicHeight = 42;
  const step = rows === 1 ? 0 : 8;
  return rows * step + basicHeight;
};

/**
 *
 * @name getServiceTypeName
 * @description Получаем имя сервис типа  (Subscription, Facebook likes и т.д)
 * @param {string|number} type
 * @returns {string|Error|*}
 */
export const getServiceTypeName = (type) => {
  const types = modifierServiceTypesToObject();
  const typeNumber = _.isNumber(type);
  if (_.isNaN(typeNumber)) {
    return new Error(`Not valid type, current - ${typeof type}`);
  }
  if (types[type]) {
    return types[type].name;
  }
  return new Error('Not found type obj');
};

/**
 *
 * @name getServiceType
 * @description Получаем выбранный service type
 * @param {string|number} serviceType
 * @returns {string|Error|*}
 */
export const getServiceType = (serviceType) => {
  const types = modifierServiceTypesToObject();
  const typeNumber = _.isNumber(serviceType);
  if (_.isNaN(typeNumber)) {
    return new Error(`Not valid type, current - ${typeof type}`);
  }
  if (types[serviceType]) {
    return types[serviceType];
  }
  return new Error('Not found type obj');
};

/**
 *
 * @name getHighlightingText
 * @description Получаем текст с подсветкой
 * @param {string} value - текст
 * @param {string|null} search - Строка поиск
 * @param {boolean} isHighlight - Подсвечивать или нет
 * @returns {{__html: string}}
 */
export const getHighlightingText = (value, search, isHighlight = true) => {
  const valueToString = value.toString();
  const normalizeMatch = valueToString
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036F]/g, '')
    .match(
      escapeStringRegexp(search.toLowerCase())
        .normalize('NFKD')
        .replace(/[\u0300-\u036F]/g, '')
        .toLowerCase(),
    );
  const matchLength = normalizeMatch?.[0]?.length;
  const matchStartIndex = normalizeMatch?.index;
  const originMatchValue = valueToString.substring(matchStartIndex, matchStartIndex + matchLength);
  if (search && isHighlight) {
    return {
      __html: matchLength
        ? `${valueToString.substring(0, matchStartIndex)}<mark>${originMatchValue}</mark>${valueToString.substring(matchStartIndex + matchLength)}`
        : value,
    };
  }
  return {
    __html: `<span>${value}</span>`,
  };
};
/**
 * @name checkIsStrictSearch
 * @description - Искать точное совпадение
 * @param {string} searchOption
 * @returns {boolean}
 */
export const checkIsStrictSearch = (searchOption) => {
  const option = SEARCH_OPTIONS.find((option) => option.value === searchOption);
  return !!(option && option.strict);
};

/**
 * @name getSelectedSearchOption
 * @description - Получаем выбранную опцию для поиска
 * @param {string} searchOption
 * @returns {object|null}
 */
export const getSelectedSearchOption = (searchOption) => {
  if (searchOption) {
    const option = SEARCH_OPTIONS.find((option) => option.value === searchOption);
    if (option) {
      return option;
    }
  }
  return null;
};

/**
 * @name filterSearchOptionsByAccess
 * @description - Фильтруем список в завимисоти от access
 * @param {object} config
 * @returns {array}
 */
export const filterSearchOptionsByAccess = (config) => {
  if (config?.access) {
    return SEARCH_OPTIONS.filter((option) => {
      if (option.value === 'external-id') {
        if (!config.access.see_provider_column || config.access.hide_provider) {
          return false;
        }
      }
      return true;
    });
  }
  return [];
};

/**
 * @name getSearchMatches
 * @description - Поиск похожих значений в массиве
 * @param {string|number} search
 * @param {array} values
 * @param {boolean} isStrict
 * @returns {boolean}
 */
export const getSearchMatches = (search, values, isStrict = false) => {
  if (search && values && _.isArray(values)) {
    const searchToLowerCase = search
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036F]/g, '');
    if (isStrict) {
      const match = values.find((item) => item.toLowerCase() === searchToLowerCase.trim());
      return !!match;
    }
    const match = values.find(
      (item) => item
        .normalize('NFKD')
        .replace(/[\u0300-\u036F]/g, '')
        .toLowerCase()
        .indexOf(searchToLowerCase) !== -1,
    );
    return !!match;
  }
  return false;
};

/**
 * @name getFormattedSearchValue
 * @description Приводим строку поиска к нужному виду
 * @param {string|number} value
 * @returns {string}
 */
export const getFormattedSearchValue = (value) => value.toString().toLowerCase();

/**
 *
 * @name getCheckingActiveFilter
 * @description Проверяем в объекте с фильтрами, есть ли активные
 * @param {object} filters
 * @returns {boolean}
 */
export const getCheckingActiveFilter = (filters) => {
  if (filters) {
    return Object.values(filters)
      .map((filter) => !_.isNull(filter.value))
      .some((filter) => filter === true);
  }
  return false;
};

/**
 * @name getOptionIcon
 * @description - Выдаем нужные классы и текст тултипа для иконок в листинге
 * @param option
 * @returns {null|object}
 */
export const getOptionIcon = (option) => {
  const icons = {
    refill: {
      tooltip: 'Refill allowed',
      className: 'far fa-sync-alt',
    },
    refill_check: {
      tooltip: 'Paid order (Refiller) enabled',
      className: 'far fa-sync-alt text-primary',
    },
    refill_manual: {
      tooltip: 'Manual refill allowed. Refill check is enabled.',
      className: 'far fa-sync-alt text-primary',
    },
    dripfeed: {
      tooltip: 'Drip-feed enabled',
      className: 'fas fa-tint',
    },
    cancel: {
      tooltip: 'Cancel allowed',
      className: 'far fa-ban',
    },
    cancel_manual: {
      tooltip: 'Manual cancel allowed',
      className: 'far fa-ban text-primary',
    },
    start_count: {
      tooltip: 'Start count parsing enabled',
      className: 'far fa-bullseye-pointer',
    },
    auto_complete: {
      tooltip: 'Auto complete enabled',
      className: 'far fa-stopwatch',
    },
  };
  if (option && icons[option]) {
    return icons[option];
  }
  return null;
};

/**
 * @name getMassActionList
 * @description - Получаем список Mass actions с учетом access доступов
 * @param {object} config
 * @returns {[]|Error}
 */
export const getMassActionList = (config) => {
  const actionsList = [
    {
      title: 'Enable all',
      accessKey: 'change_service_status',
      value: MODALS.ENABLE_ALL,
    },
    {
      title: 'Disable all',
      accessKey: 'change_service_status',
      value: MODALS.DISABLE_ALL,
    },
    {
      title: 'Edit rates',
      accessKey: 'edit_services',
      value: MODALS.EDIT_RATES,
    },
    {
      title: 'Assign category',
      accessKey: 'edit_services',
      value: MODALS.ASSIGN_CATEGORY,
    },
    {
      title: 'Update currency conversion rate',
      accessKey: 'edit_services',
      value: MODALS.UPDATE_CURRENCY_CONVERSION_RATE,
    },
    {
      title: 'Drip-feed',
      accessKey: 'edit_services',
      value: MODALS.CHANGE_DRIP_FEED_ALL,
      subMenu: [
        {
          title: 'Enable for all',
          value: MODALS.CHANGE_DRIP_FEED_ALL,
          data: {
            type: 1,
          },
        },
        {
          title: 'Disable for all',
          value: MODALS.CHANGE_DRIP_FEED_ALL,
          data: {
            type: 0,
          },
        },
      ],
    },
    {
      title: 'Delete custom rates',
      accessKey: 'reset_custom_rates',
      value: MODALS.RESET_CUSTOM_RATES_ALL,
    },
    {
      title: 'Delete all',
      accessKey: 'delete_service',
      value: MODALS.DELETE_ALL,
    },
  ];
  if (config && config.access) {
    return actionsList.filter((action) => !!config.access[action.accessKey]);
  }
  return new Error('Not found config access');
};

/**
 *
 * @name getCategoryActionList
 * @description Получаем список Action категории с учетом access доступов
 * @param {object} category - Категория
 * @param {object} config
 * @returns {[]|Error}
 */
export const getCategoryActionList = (category, config) => {
  const actionsList = [
    {
      title: 'Edit category',
      accessKey: 'edit_category',
      value: MODALS.EDIT_CATEGORY,
    },
    {
      title: `${category.visibility ? 'Disable' : 'Enable'} category`,
      accessKey: 'change_category_status',
      value: MODALS.CHANGE_CATEGORY_VISIBILITY,
    },
  ];

  if (category.services.length) {
    actionsList.push({
      title: 'Sort by',
      accessKey: 'edit_services',
      value: null,
      subMenu: [
        {
          title: 'Rate (low to high)',
          value: ACTION.SORT_POSITION_LOW_TO_HIGH,
          id: category.id,
        },
        {
          title: 'Rate (high to low)',
          value: ACTION.SORT_POSITION_HIGH_TO_LOW,
          id: category.id,
        },
      ],
    });
    actionsList.push({
      title: 'Select all category services',
      accessKey: 'edit_services',
      value: 'CATEGORY_SERVICES_SELECT',
    });
  }

  if (!category?.services.length) {
    actionsList.push({
      title: 'Delete category',
      accessKey: 'edit_category',
      value: MODALS.DELETE_CATEGORY,
    });
  }

  if (config && config.access) {
    return actionsList.filter((action) => !!config.access[action.accessKey]);
  }
  return new Error('Not found config access');
};

/**
 *
 * @name getServiceActionList
 * @description Получаем список Action сервиса с учетом access доступов
 * @param {object} service - Данные сервиса
 * @param {object} config
 * @returns {[]|Error}
 */
export const getServiceActionList = (service, config) => {
  const actionsList = [
    {
      title: 'Edit service',
      accessKey: 'edit_services',
      value: MODALS.EDIT_SERVICE,
    },
    {
      title: 'Edit description',
      accessKey: 'edit_services_description',
      value: MODALS.EDIT_DESCRIPTION,
    },
    {
      title: `${service.status === 1 ? 'Disable' : 'Enable'} service`,
      accessKey: 'change_service_status',
      value: MODALS.CHANGE_STATUS,
    },
    {
      title: 'Delete custom rates',
      accessKey: 'reset_custom_rates',
      value: MODALS.RESET_CUSTOM_RATES,
    },
    {
      title: 'Duplicate',
      accessKey: 'duplicate_service',
      value: MODALS.DUPLICATE,
    },
    {
      title: 'Delete service',
      accessKey: 'delete_service',
      value: MODALS.DELETE,
    },
  ];
  if (config && config.access) {
    return actionsList.filter((action) => !!config.access[action.accessKey]);
  }
  return new Error('Not found config access');
};

/**
 *
 * @name getErrorMessage
 * @description Получаем текст ошибки
 * @param {object|string} response
 * @returns {string}
 */
export const getErrorMessage = (response) => {
  if (response) {
    if (_.isString(response)) {
      return response;
    }
    if (response.hasOwnProperty('message')) {
      return response.message;
    }
    if (response.hasOwnProperty('status')) {
      const { status } = response;
      switch (status) {
      case 404:
        if (response.data && response.data.error_message) {
          const { error_message } = response.data;
          return error_message;
        }
        return 'Route note found';
      default:
        if (response.data && response.data.error_message) {
          const { error_message } = response.data;
          return error_message;
        }
      }
    }
  }
  return 'Unknown error. Try again later.';
};

/**
 *
 * @name getCategoryDropUp
 * @description Проверяем, надо ли развернуть actions КАТЕГОРИИ в другую сторону
 * @param {object} category
 * @param {number} currentIndex
 * @param {number} categoryLength
 * @param {number} servicesLength
 * @returns {boolean}
 */
export const getCategoryDropUp = (category, currentIndex, categoryLength, servicesLength) => {
  const isLast = categoryLength - (currentIndex + 1) < 2;
  if (currentIndex === 0) {
    return false;
  }

  if (!category.show && isLast) {
    return true;
  }

  if (isLast) {
    return servicesLength < 2;
  }

  return false;
};

/**
 *
 * @name getServiceDropUp
 * @description - Проверяем, надо ли развернуть actions СЕРВИСОВ в другую сторону
 * @param {number} categoryIndex
 * @param {number} categoryLength
 * @param {number} currentIndex
 * @param {number} servicesLength
 * @param {boolean} showNextCategory
 * @param {array} categories
 * @returns {boolean}
 */
export const getServiceDropUp = (categoryIndex, categoryLength, currentIndex, servicesLength, showNextCategory, categories) => {
  if (categoryLength + servicesLength < 5) {
    return false;
  }
  const isLastCategory = categoryLength - (categoryIndex + 1) < (showNextCategory ? 1 : 2);
  if (isLastCategory) {
    if (categoryIndex !== 0) {
      if (!categories[categoryIndex - 1].show && currentIndex + 1 < 3) {
        return false;
      }
      return servicesLength - (currentIndex + 1) < 4;
    }
  }
  return false;
};

/**
 * @name getServicesListSortByRate
 * @description Сортирвка позиции сервисов по rate
 * @param {string} sortType
 * @param {number} categoryId
 * @returns {Error|*}
 */
export const getServicesListSortByRate = (sortType, categoryId) => {
  if (categoryId && sortType) {
    const {
      services: { categories },
    } = store.getState();

    const category = categories.find((category) => category.id === categoryId);

    if (category) {
      let sortServices = _.cloneDeep(category.services);
      if (sortType === ACTION.SORT_POSITION_LOW_TO_HIGH) {
        sortServices = sortServices.sort(sortByRateAsc);
      }
      if (sortType === ACTION.SORT_POSITION_HIGH_TO_LOW) {
        sortServices = sortServices.sort(sortByRateDesc);
      }

      if (sortServices?.length) {
        return sortServices.map((service, index) => ({
          id: service.id,
          position: index,
        }));
      }
    }
    return new Error('Not find categoryID');
  }
  return new Error('categoryId or sortType is undefined');
};

/**
 *
 * @name getBoolRoundingRate
 * @description Проверяем надо ли, округлять rate
 * @param {string|number} rate
 * @returns {boolean}
 */
export const getBoolRoundingRate = (rate) => {
  if (rate) {
    const rateToString = rate.toString();
    return rateToString.length > 8;
  }
  return false;
};

/**
 *
 * @name getRoundingRate
 * @description Получаем округленный Rate
 * @param {string|number} rate
 * @returns {string|*}
 */
export const getRoundingRate = (rate) => {
  if (rate) {
    return `≈ ${rate.substr(0, 8)}`;
  }
  return rate;
};

/**
 *
 * @name getAtLeastOneService
 * @description Проверяем, есть ли хоть один сервис
 * @param {array} list
 * @returns {boolean}
 */
export const getAtLeastOneService = (list) => {
  if (list && _.isArray(list)) {
    const findService = list.find((row) => row.collection === 'service');
    if (findService) {
      return false;
    }
  }
  return true;
};

/**
 *
 * @name getSortedCategory
 * @description Получаем отсортированный список категорий
 * @param {array} list - Объединенный список (категории + сервисы)
 * @param {array} startList - Список категорий
 * @param {number} oldIndex
 * @param {number} newIndex
 * @returns {{id: *, position: *}[]|Error}
 */
export const getSortedCategory = async (list, startList, oldIndex, newIndex) => {
  if (list && list.length) {
    if (list[oldIndex]) {
      const newOldIndex = list[oldIndex].categoryIndex;
      const newNewIndex = list[newIndex].categoryIndex;
      const sortedCategories = arrayMove(startList, newOldIndex, newNewIndex);
      return sortedCategories.map((category, index) => ({
        id: category.id,
        position: index,
      }));
    }
  }
  return new Error('Category list is empty');
};

/**
 *
 * @name getSortedServices
 * @description Получаем отсортированный список сервисов
 * @param {array} list - Объединенный список (категории + сервисы)
 * @param {array} startList
 * @param {number} oldIndex
 * @param {number} newIndex
 * @returns {{id: *, position: *}[]|Error}
 */
export const getSortedServices = async (list, startList, oldIndex, newIndex) => {
  if (list && list.length) {
    if (list[oldIndex]) {
      const newOldIndex = list[oldIndex].serviceIndex;
      const newNewIndex = list[newIndex].serviceIndex;
      const { startListCategoryIndex } = list[oldIndex];
      if (startList[startListCategoryIndex]) {
        const category = startList[startListCategoryIndex];
        if (category.services) {
          const servicesList = category.services;
          const sortedServicesList = arrayMove(servicesList, newOldIndex, newNewIndex);
          return sortedServicesList.map((services, index) => ({
            id: services.id,
            position: index,
          }));
        }
      }
    }
  }
  return new Error('Not found category');
};

/**
 * Проверка цены относительно провайдера
 * @param rate
 * @param isSameProviderCurrency
 * @returns {boolean}
 */
export const isDangerRate = (rate, isSameProviderCurrency) => {
  if (rate && _.isObject(rate)) {
    if (rate.converted && rate.custom && !isSameProviderCurrency) {
      const convertedRate = parseFloat(rate.converted);
      const customRate = parseFloat(rate.custom);
      return convertedRate > customRate;
    }
    if (rate.provider && rate.custom) {
      const providerRate = parseFloat(rate.provider);
      const customRate = parseFloat(rate.custom);
      return providerRate > customRate;
    }
  }
  return false;
};

/**
 * Категория с задизейбленным стилем
 * Таска PNL-9569
 * В случае если в категории отсутствуют услуги или все услуги в категории имеют статус Disabled -- отображать в админке такую категорию, так же как мы отображаем скрытые категории (при использовании Disable category - категория отображается другим цветом).
 * @param category
 * @returns {boolean}
 */
export const isStyleDisableCategory = (category) => {
  if (category?.services.length) {
    const { services } = category;
    const findOneEnableService = services.find((service) => service.status === 1);
    return !findOneEnableService;
  }
  return true;
};
